import type { EventManager } from '@paperbits/common/events/eventManager';
import type { RouteGuard, Router } from '@paperbits/common/routing';
import { RouterEvents } from '@paperbits/common/routing/routerEvents';

import type { Route } from './route';


export class CustomRouter implements Router {
    private notificationTimeout: any;
    public currentRoute: Route;
    public notifyListeners: boolean;

    constructor(
        private readonly routeGuards: RouteGuard[],
        private readonly eventManager: EventManager
    ) {
        this.notifyListeners = true;
        this.currentRoute = this.getRouteFromLocation();
    }

    public getRouteFromLocation(): Route {
        const path = location.pathname;
        const hash = location.hash.startsWith('#') ? location.hash.slice(1) : null;
        const searchParams = new URLSearchParams(location.search);
        const url = path + location.search + (hash ? `#${hash}` : '');

        return {
            url: url,
            path: path,
            searchParams: Array.from((searchParams as unknown as {entries: () => Iterable<[string, string]>}).entries()),
            hash: hash,
            metadata: {},
            previous: null
        };
    }

    public addRouteChangeListener(eventHandler: (args?: any) => void): void {
        this.eventManager.addEventListener(RouterEvents.onRouteChange, eventHandler);
    }

    public removeRouteChangeListener(eventHandler: (args?: any) => void): void {
        this.eventManager.removeEventListener(RouterEvents.onRouteChange, eventHandler);
    }

    public addHistoryUpdateListener(eventHandler: (args?: any) => void): void {
        this.eventManager.addEventListener(RouterEvents.onHistoryUpdate, eventHandler);
    }

    public removeHistoryUpdateListener(eventHandler: (args?: any) => void): void {
        this.eventManager.removeEventListener(RouterEvents.onHistoryUpdate, eventHandler);
    }

    /**
     * Navigates to specified URL.
     * @param url Absolute or relative path, i.e. https://paperbits.io or /about
     * @param title Destination title
     * @param metadata Associated metadata
     */
    public async navigateTo(url: string, title: string = null, metadata: object = {}): Promise<void> {
        const route = this.getRoute(url, title, metadata);

        if (!route) {
            return;
        }

        const canActivate = await this.canActivate(route);

        if (canActivate) {
            this.currentRoute = route;

            if (this.notifyListeners) {
                this.scheduleNotification(route);
            }
        }
    }

    private scheduleNotification(route: Route): void {
        clearTimeout(this.notificationTimeout);

        this.notificationTimeout = setTimeout(() => {
            this.eventManager.dispatchEvent(RouterEvents.onRouteChange, route);
        }, 100);
    }

    public updateHistory(url: string, title: string): void {
        const route = this.getRoute(url, title);

        if (!route) {
            return;
        }

        this.currentRoute = route;

        if (this.notifyListeners) {
            this.eventManager.dispatchEvent(RouterEvents.onHistoryUpdate, route);
        }
    }

    public addToSearch(params: object): void {
        const location = window.location;
        const search = new URLSearchParams(location.search);
        for (const [k, v] of Object.entries(params)) {
            search.delete(k);
            search.append(k, v);
        }
        const newUrl = `${location.pathname}?${search.toString()}${location.hash}`;
        this.updateHistory(newUrl, null);
    }

    private getRoute(url: string, title: string = null, metadata: object = {}): Route {
        if (!url) {
            return undefined;
        }

        const isFullUrl = url && (url.startsWith('http://') || url.startsWith('https://'));
        const isMailUrl = url?.startsWith('mailto:');
        const isBlobUrl = url?.startsWith('blob:');
        const isTelUrl = url?.startsWith('tel:');
        const isLocalUrl = url.startsWith(location.origin);

        if (isFullUrl && !isLocalUrl) {
            window.open(url, '_blank'); // navigating external link
            return;
        }

        const parsed = new URL(url, location.origin);

        const searchParams: URLSearchParams = parsed.searchParams;
        const path = isMailUrl || isBlobUrl || isTelUrl ? url : parsed.pathname;

        return {
            url: url,
            path,
            title: title,
            metadata: metadata,
            searchParams: Array.from((searchParams as unknown as {entries: () => Iterable<[string, string]>}).entries()),
            hash: parsed.hash.startsWith('#') ? parsed.hash.slice(1) : parsed.hash,
            previous: this.currentRoute
        };
    }

    protected async canActivate(route: Route): Promise<boolean> {
        for (const routeGuard of this.routeGuards) {
            try {
                const canActivate = await routeGuard.canActivate(route);

                if (!canActivate) {
                    return false;
                }
            } catch (error) {
                throw new Error(`Unable to invoke route a guard: ${error.stack || error.message}`);
            }
        }

        return true;
    }

    public getCurrentUrl(): string {
        let permalink = this.currentRoute.path;

        const searchParams = this.currentRoute.searchParams.toString();

        if (searchParams) {
            permalink += `?${searchParams}`;
        }

        const hash = this.getHash();

        if (this.currentRoute.hash) {
            permalink += `#${hash}`;
        }

        return permalink;
    }

    public getCurrentUrlMetadata(): object {
        return this.currentRoute.metadata;
    }

    public getPath(): string {
        return this.currentRoute.path;
    }

    public getHash(): string {
        return this.currentRoute.hash;
    }

    public getCurrentRoute(): Route {
        return this.currentRoute;
    }

    public getSearchParams(): [string, string][] {
        return this.currentRoute.searchParams;
    }
}